import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RunStatus } from '@rossum/api-client/hooks';
import { Box, Stack, Typography } from '@rossum/ui/material';
import {
  getActivityDuration,
  isRunningJob,
  TransformedActivity,
} from '../../helpers';
import { DurationDotIndicator } from '../DurationDotIndicator';
import { STATUSES_DATA } from './StatusColumn';
import { TextColumn } from './TextColumn';

const RUNNING_INTERVAL = 1000;

type RunningDurationProps = {
  start: string | undefined;
  status: Extract<RunStatus, 'running' | 'waiting'>;
};

const RunningDuration = ({ start, status }: RunningDurationProps) => {
  const intl = useIntl();
  const [endOfDuration, setEndOfDuration] = useState(new Date().toISOString());

  useEffect(() => {
    const updateDuration = () => setEndOfDuration(new Date().toISOString());
    const interval = setInterval(updateDuration, RUNNING_INTERVAL);

    updateDuration();

    return () => clearInterval(interval);
  }, []);

  const duration = start
    ? getActivityDuration(
        start,
        endOfDuration,
        intl.formatMessage({
          id: 'features.activities.columns.duration.lessThanOneSecond',
        })
      )
    : '-';

  return (
    <Stack
      width={1}
      aria-label={`Activity duration: ${duration}`}
      justifyContent="center"
      position="relative"
    >
      <Box position="absolute" left={15}>
        <DurationDotIndicator color={STATUSES_DATA[status].color} />
      </Box>
      <Typography color="secondary" variant="body2" textAlign="center">
        {duration}
      </Typography>
    </Stack>
  );
};

type DurationColumnProps = TransformedActivity;

export const DurationColumn = ({
  start,
  end,
  status,
  cancelRequested,
}: DurationColumnProps) => {
  const intl = useIntl();
  const isRunning = isRunningJob(status);

  if (isRunning && !cancelRequested) {
    return <RunningDuration start={start ?? undefined} status={status} />;
  }

  const runEnd = end ?? cancelRequested;
  const terminatedDuration =
    start && runEnd
      ? getActivityDuration(
          start,
          runEnd,
          intl.formatMessage({
            id: 'features.activities.columns.duration.lessThanOneSecond',
          })
        )
      : '-';

  return (
    <TextColumn
      value={terminatedDuration}
      aria-label={`Activity duration: ${terminatedDuration}`}
    />
  );
};
